import { useTranslations } from 'next-intl'
import { useCallback, useMemo } from 'react'
import useClevertapForSrp from '../../analytics/clevertap/srp'
import useFormatCurrencyForSrp from '../../currency/useFormatCurrencyForSrp'
import useUpdateSearchResultCache from '../updateSearchResultCache'
import useSearchResults from '../useSearchResults'

export interface Airline {
    id: string
    name: string
    logoUrl: string
    price: string
    isSelected?: boolean
    unFormattedPrice: number
}

/**
 * multiple instance of this hooks always return updated data as it's only dependent on redux state and actions , don't create any local state
 */

export const useAirlineFilterManager = (journeyLeg: 0 | 1 = 0) => {
    const t = useTranslations('srpFilters')
    const title = t('airline')
    const updateCache = useUpdateSearchResultCache()
    const { formatCurrency } = useFormatCurrencyForSrp()
    const { filterData, selectedJourneyLeg } = useSearchResults(journeyLeg)
    const { triggerSrpEvent } = useClevertapForSrp()
    const { airlineFilter: airlineFilters } = filterData || {}
    const airlineFilter = airlineFilters?.[selectedJourneyLeg]
    const { selectedValues: selectedAirlines = [] } = airlineFilter || {}
    const airlines: Airline[] | undefined = useMemo(
        () =>
            airlineFilter?.options
                .map(option => ({
                    id: option.value,
                    name: option.label,
                    logoUrl: `https://ui.cltp.co/images/logos/air-logos/svg_logos/${option.value}.svg`,
                    price: !isNaN(Number(option.priceLabel)) ? formatCurrency(option.priceLabel!) : '',
                    isSelected: selectedAirlines?.includes(option.value),
                    unFormattedPrice: option.priceLabel || 0
                }))
                .sort((a, b) => {
                    //name can be undefined
                    if (!a.name || !b.name) return 0
                    return a.name.localeCompare(b.name)
                }),
        [airlineFilter?.options, selectedAirlines, formatCurrency]
    )
    const handleSelectToggle = useCallback(
        (id: string, filterAppliedSource = '') => {
            const isRemoving = selectedAirlines?.includes(id)
            const selectedValues = isRemoving
                ? selectedAirlines?.filter(s => s !== id) || []
                : [...(selectedAirlines || []), id]

            triggerSrpEvent('Air_UI_Action', {
                action_name: 'filter',
                action_type: 'airline',
                action_value: selectedValues.join(',') || 'none',
                filterAppliedSource
            })

            if (isRemoving) {
                //update rtk cache
                updateCache(currentData => {
                    if (currentData?.filterData?.airlineFilter?.[selectedJourneyLeg]) {
                        currentData.filterData.airlineFilter[selectedJourneyLeg].selectedValues =
                            selectedAirlines.filter(s => s !== id)
                    }
                    return currentData as NonNullable<typeof currentData>
                })
            } else {
                updateCache(currentData => {
                    if (currentData?.filterData?.airlineFilter?.[selectedJourneyLeg]) {
                        currentData.filterData.airlineFilter[selectedJourneyLeg].selectedValues.push(id)
                    }
                    return currentData as NonNullable<typeof currentData>
                })
            }
        },
        [selectedAirlines, selectedJourneyLeg, triggerSrpEvent]
    )
    const handleReset = useCallback(() => {
        triggerSrpEvent('Air_UI_Action', {
            action_name: 'filter',
            action_type: 'reset',
            action_value: 'airline'
        })

        updateCache(currentData => {
            if (currentData?.filterData?.airlineFilter?.[selectedJourneyLeg]) {
                currentData.filterData.airlineFilter[selectedJourneyLeg].selectedValues = []
            }
            return currentData as NonNullable<typeof currentData>
        })
    }, [selectedJourneyLeg, triggerSrpEvent])
    const shouldShowReset = useMemo(() => {
        return !!selectedAirlines?.length
    }, [selectedAirlines])
    return {
        airlines,
        handleSelectToggle,
        handleReset,
        shouldShowReset,
        title
    }
}

export default useAirlineFilterManager
